import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, ClipboardList, AlertTriangle, CheckCircle2, FileCheck, Briefcase,
  DollarSign, Wrench, CalendarClock, Shield, TrendingUp, TrendingDown,
  Clock, AlertCircle, ChevronRight, RefreshCw, Loader2, Filter,
  BarChart3, PieChart, Activity, Users, ArrowLeft, HelpCircle
} from 'lucide-react';
import { StatCard, StatCardGrid } from '../components/StatCard';
import { ClientFacilityFilter } from '../components/FilterBar';

// Simple chart components (no external library needed)
const DonutChart = ({ data, colors, size = 120 }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  if (total === 0) {
    return (
      <div className="flex items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-slate-400 text-sm">No data</span>
      </div>
    );
  }
  
  // Calculate all segments with their angles
  const segments = [];
  let accumulatedAngle = 0;
  
  data.forEach((item, index) => {
    const percentage = item.value / total;
    const angle = percentage * 360;
    const startAngle = accumulatedAngle;
    accumulatedAngle += angle;
    
    const x1 = 50 + 40 * Math.cos((startAngle - 90) * Math.PI / 180);
    const y1 = 50 + 40 * Math.sin((startAngle - 90) * Math.PI / 180);
    const x2 = 50 + 40 * Math.cos((startAngle + angle - 90) * Math.PI / 180);
    const y2 = 50 + 40 * Math.sin((startAngle + angle - 90) * Math.PI / 180);
    
    const largeArc = angle > 180 ? 1 : 0;
    
    segments.push(
      <path
        key={index}
        d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
        fill={colors[index % colors.length]}
        className="transition-opacity hover:opacity-80"
      />
    );
  });
  
  return (
    <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
      {segments}
      <circle cx="50" cy="50" r="25" fill="white" className="dark:fill-slate-800" />
      <text x="50" y="50" textAnchor="middle" dy="0.3em" className="text-lg font-bold fill-slate-900 dark:fill-white">
        {total}
      </text>
    </svg>
  );
};

const HorizontalBar = ({ label, value, maxValue, color, onClick }) => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <div 
      className={`mb-2 ${onClick ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 rounded p-1 -m-1' : ''}`}
      onClick={onClick}
    >
      <div className="flex justify-between text-sm mb-1">
        <span className="text-slate-600 dark:text-slate-300">{label}</span>
        <span className="font-semibold text-slate-900 dark:text-white">{value.toLocaleString()}</span>
      </div>
      <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const TrendLine = ({ data, color = 'text-teal-600', height = 40 }) => {
  if (!data || data.length === 0) return <span className="text-slate-400 text-xs">No data</span>;
  
  const max = Math.max(...data.map(d => d.count), 1);
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1 || 1)) * 100;
    const y = height - (d.count / max) * height;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg viewBox={`0 0 100 ${height}`} className="w-full" style={{ height }}>
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={color}
      />
    </svg>
  );
};

const formatCurrency = (value) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

// Forecast Bar Chart Component
const ForecastBarChart = ({ data, height = 200 }) => {
  if (!data || data.length === 0) {
    return <div className="text-center text-slate-400 py-8">No forecast data available</div>;
  }
  
  const maxCost = Math.max(...data.map(d => d.cost), 1);
  const currentYear = new Date().getFullYear();
  
  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-[800px]" style={{ height }}>
        <div className="flex items-end justify-between h-full gap-1 px-2">
          {data.map((item, idx) => {
            const barHeight = maxCost > 0 ? (item.cost / maxCost) * (height - 40) : 0;
            const isCurrentYear = item.year === currentYear;
            const hasValue = item.cost > 0;
            
            return (
              <div key={item.year} className="flex flex-col items-center flex-1 min-w-[35px]">
                {/* Cost label */}
                <div className="text-xs text-slate-500 mb-1 h-4 flex items-end">
                  {hasValue && formatCurrency(item.cost)}
                </div>
                {/* Bar */}
                <div 
                  className={`w-full rounded-t transition-all ${
                    isCurrentYear ? 'bg-teal-500' : 
                    hasValue ? 'bg-sky-400 hover:bg-sky-500' : 'bg-slate-200 dark:bg-slate-700'
                  }`}
                  style={{ height: Math.max(barHeight, 2) }}
                  title={`${item.year}: ${formatCurrency(item.cost)}`}
                />
                {/* Year label */}
                <div className={`text-xs mt-1 ${isCurrentYear ? 'font-bold text-teal-600' : 'text-slate-400'}`}>
                  {item.year.toString().slice(-2)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const UNIFORMAT_LABELS = {
  'A': 'Substructure',
  'B': 'Shell',
  'C': 'Interiors',
  'D': 'Services',
  'E': 'Equipment',
  'F': 'Furnishings',
  'G': 'Sitework',
  'Unknown': 'Other'
};

// Format FCI with appropriate decimal places based on value
const formatFCI = (fciPercent) => {
  if (fciPercent === 0 || fciPercent === null || fciPercent === undefined) {
    return '0.0000';
  }
  // Show 4 decimal places for values under 1%, 2 for values under 10%, 1 otherwise
  if (fciPercent < 0.01) {
    return fciPercent.toFixed(4);
  } else if (fciPercent < 1) {
    return fciPercent.toFixed(4);
  } else if (fciPercent < 10) {
    return fciPercent.toFixed(2);
  }
  return fciPercent.toFixed(1);
};

export const DashboardPage = () => {
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedModule, setSelectedModule] = useState('all'); // all, fca, iwms
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedFacility, setSelectedFacility] = useState('');

  const fetchStats = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (selectedClient) params.append('client_id', selectedClient);
      if (selectedFacility) params.append('facility_id', selectedFacility);
      const queryString = params.toString();
      const url = `${API}/dashboard/stats${queryString ? `?${queryString}` : ''}`;
      const response = await axios.get(url);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [API, selectedClient, selectedFacility]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const navigateWithBack = (path, state = {}) => {
    navigate(path, { state: { ...state, fromDashboard: true } });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-slate-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const fciColor = stats?.financials?.fci_rating === 'Good' ? 'text-emerald-600' :
                   stats?.financials?.fci_rating === 'Fair' ? 'text-amber-500' :
                   stats?.financials?.fci_rating === 'Poor' ? 'text-orange-500' : 'text-red-600';

  const priorityColors = ['#ef4444', '#f97316', '#eab308', '#22c55e']; // critical, high, medium, low
  const deficiencyData = [
    { label: 'Critical', value: stats?.fca?.deficiencies_by_priority?.critical || 0 },
    { label: 'High', value: stats?.fca?.deficiencies_by_priority?.high || 0 },
    { label: 'Medium', value: stats?.fca?.deficiencies_by_priority?.medium || 0 },
    { label: 'Low', value: stats?.fca?.deficiencies_by_priority?.low || 0 },
  ];

  const maxDeficiency = Math.max(...deficiencyData.map(d => d.value), 1);
  const maxCost = Math.max(...Object.values(stats?.financials?.cost_by_priority || {}), 1);

  return (
    <div className="p-4 md:p-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
            Dashboard
          </h1>
          <p className="text-slate-500 dark:text-slate-300 mt-1">
            Welcome back, {user?.full_name}
          </p>
        </div>
        <div className="flex items-center gap-3 mt-4 md:mt-0">
          {/* Module Filter */}
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            {[
              { key: 'all', label: 'All' },
              { key: 'fca', label: 'Assessment' },
              { key: 'iwms', label: 'Work Mgmt' }
            ].map(mod => (
              <button
                key={mod.key}
                onClick={() => setSelectedModule(mod.key)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  selectedModule === mod.key
                    ? 'bg-white dark:bg-slate-700 text-teal-600 shadow-sm'
                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900'
                }`}
              >
                {mod.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchStats(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Client/Facility Filters */}
      {stats?.filter_options && (stats.filter_options.clients?.length > 0 || stats.filter_options.facilities?.length > 0) && (
        <div className="flex flex-wrap items-center gap-3 mb-6 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
          <ClientFacilityFilter
            clients={stats.filter_options.clients || []}
            facilities={stats.filter_options.facilities || []}
            selectedClient={selectedClient}
            selectedFacility={selectedFacility}
            onClientChange={(value) => {
              setSelectedClient(value === 'all' ? '' : value);
              setSelectedFacility('');
            }}
            onFacilityChange={(value) => setSelectedFacility(value === 'all' ? '' : value)}
            onClear={() => {
              setSelectedClient('');
              setSelectedFacility('');
            }}
          />
          
          {/* Applied Filter Badge */}
          {(selectedClient || selectedFacility) && (
            <Badge variant="secondary" className="ml-auto">
              Filtered
            </Badge>
          )}
        </div>
      )}

      {/* Data Transparency Indicator */}
      {stats?.assets && stats.assets.total > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg text-sm cursor-help" data-testid="data-transparency-indicator">
                <BarChart3 size={16} className="text-sky-600" />
                <span className="text-slate-700 dark:text-slate-300">
                  Dashboard includes data from{' '}
                  <span className="font-semibold text-emerald-600">{stats.assets.approved || 0} approved</span>
                  {(stats.assets.locked || 0) > 0 && (
                    <>, <span className="font-semibold text-amber-600">{stats.assets.locked} pending review</span></>
                  )}
                  {(stats.assets.draft || 0) > 0 && (
                    <>, <span className="font-semibold text-slate-500">{stats.assets.draft} draft</span></>
                  )} assets
                </span>
                <HelpCircle size={14} className="text-slate-400 ml-1" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-sm bg-slate-900 text-white p-3">
              <p className="font-semibold mb-2">Asset Data Breakdown</p>
              <div className="text-xs space-y-1">
                <p><span className="text-emerald-400">Approved ({stats.assets.approved || 0}):</span> SME-verified, IWMS-ready</p>
                <p><span className="text-amber-400">Pending Review ({stats.assets.locked || 0}):</span> Submitted, awaiting SME</p>
                <p><span className="text-slate-400">Draft ({stats.assets.draft || 0}):</span> Work in progress</p>
                {(stats.assets.rejected || 0) > 0 && (
                  <p><span className="text-red-400">Rejected ({stats.assets.rejected}):</span> Needs revision</p>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-2 pt-2 border-t border-slate-700">
                Finance Hub shows only operational (approved) assets for accurate forecasting.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Key Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        {/* Portfolio Value */}
        <StatCard
          title="Portfolio Value"
          value={formatCurrency(stats?.financials?.total_replacement_value || 0)}
          icon={DollarSign}
          variant="gradient"
          gradientFrom="from-teal-500"
          gradientTo="to-teal-600"
          onClick={() => navigate('/facilities')}
          size="small"
        />

        {/* Deferred Maintenance */}
        <StatCard
          title="Deferred Maint."
          value={formatCurrency(stats?.financials?.total_deferred_maintenance || 0)}
          icon={AlertTriangle}
          variant="gradient"
          gradientFrom="from-orange-500"
          gradientTo="to-red-500"
          onClick={() => navigate('/assessments')}
          size="small"
        />

        {/* FCI Score with Tooltip */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <StatCard
                  title={
                    <span className="flex items-center gap-1">
                      FCI Score
                      <HelpCircle size={12} className="text-slate-400" />
                    </span>
                  }
                  value={`${formatFCI(stats?.financials?.fci_percent)}%`}
                  icon={Activity}
                  iconColor={fciColor}
                  badge={
                    <Badge variant={stats?.financials?.fci_rating === 'Good' ? 'default' : 'destructive'}>
                      {stats?.financials?.fci_rating || 'N/A'}
                    </Badge>
                  }
                  size="small"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs bg-slate-900 text-white p-3">
              <p className="font-semibold mb-1">Facility Condition Index (FCI)</p>
              <p className="text-xs text-slate-300 mb-2">
                FCI = Deferred Maintenance ÷ Asset Replacement Value
              </p>
              <p className="text-xs text-slate-400 mb-2 italic">
                Based on assessed asset values from FCA, not building value
              </p>
              <div className="text-xs space-y-1 border-t border-slate-700 pt-2 mt-2">
                <p><span className="text-emerald-400">Good:</span> ≤5%</p>
                <p><span className="text-amber-400">Fair:</span> 5-10%</p>
                <p><span className="text-orange-400">Poor:</span> 10-30%</p>
                <p><span className="text-red-400">Critical:</span> &gt;30%</p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Open Work Orders */}
        {(selectedModule === 'all' || selectedModule === 'iwms') && (
          <StatCard
            title="Open Work Orders"
            value={stats?.iwms?.open_work_orders || 0}
            icon={Wrench}
            iconColor="text-sky-600"
            onClick={() => navigateWithBack('/work-orders', { filter: 'open' })}
            size="small"
          />
        )}

        {/* Overdue PMs */}
        {(selectedModule === 'all' || selectedModule === 'iwms') && (
          <StatCard
            title="Overdue PMs"
            value={stats?.iwms?.overdue_pms || 0}
            icon={Clock}
            iconColor={stats?.iwms?.overdue_pms > 0 ? 'text-red-600' : 'text-slate-400'}
            variant={stats?.iwms?.overdue_pms > 0 ? 'highlight' : 'default'}
            highlightColor="red"
            isAlert={true}
            onClick={() => navigateWithBack('/pm-schedules', { filter: 'overdue' })}
            size="small"
          />
        )}

        {/* Expiring Warranties */}
        {(selectedModule === 'all' || selectedModule === 'iwms') && (
          <StatCard
            title="Expiring (30d)"
            value={stats?.iwms?.expiring_warranties || 0}
            icon={Shield}
            iconColor={stats?.iwms?.expiring_warranties > 0 ? 'text-amber-600' : 'text-slate-400'}
            variant={stats?.iwms?.expiring_warranties > 0 ? 'highlight' : 'default'}
            highlightColor="amber"
            isAlert={true}
            onClick={() => navigateWithBack('/warranties', { filter: 'expiring' })}
            size="small"
          />
        )}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        {/* Deficiencies by Priority */}
        {(selectedModule === 'all' || selectedModule === 'fca') && (
          <Card className="p-5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <h3 className="font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <ClipboardList size={18} className="text-teal-600" />
              Assessment Status
            </h3>
            <div className="space-y-3">
              <div 
                className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600"
                onClick={() => navigateWithBack('/assessments', { statusFilter: 'draft' })}
              >
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-slate-400" />
                  <span className="text-sm text-slate-600 dark:text-slate-300">Draft</span>
                </div>
                <span className="font-semibold text-slate-900 dark:text-white">{stats?.fca?.draft_assessments || 0}</span>
              </div>
              <div 
                className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600"
                onClick={() => navigateWithBack('/assessments', { statusFilter: 'in_progress' })}
              >
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-sky-500" />
                  <span className="text-sm text-slate-600 dark:text-slate-300">In Progress</span>
                </div>
                <span className="font-semibold text-slate-900 dark:text-white">{stats?.fca?.in_progress_assessments || 0}</span>
              </div>
              <div 
                className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600"
                onClick={() => navigateWithBack('/assessments', { statusFilter: 'pending_review' })}
              >
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500" />
                  <span className="text-sm text-slate-600 dark:text-slate-300">Pending Review</span>
                </div>
                <span className="font-semibold text-slate-900 dark:text-white">{stats?.fca?.pending_review_assessments || 0}</span>
              </div>
              <div 
                className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600"
                onClick={() => navigateWithBack('/assessments', { statusFilter: 'approved' })}
              >
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-sm text-slate-600 dark:text-slate-300">Approved</span>
                </div>
                <span className="font-semibold text-slate-900 dark:text-white">{stats?.fca?.approved_assessments || 0}</span>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-700">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Total Assessments</span>
                <span className="font-bold text-slate-900 dark:text-white">{stats?.fca?.total_assessments || 0}</span>
              </div>
            </div>
          </Card>
        )}

        {(selectedModule === 'all' || selectedModule === 'fca') && (
          <Card className="p-5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <h3 className="font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <PieChart size={18} className="text-teal-600" />
              Deficiencies by Priority
            </h3>
            <div className="flex items-center gap-6">
              <DonutChart data={deficiencyData} colors={priorityColors} size={100} />
              <div className="flex-1 space-y-2">
                {deficiencyData.map((item, idx) => (
                  <div 
                    key={item.label}
                    className="flex items-center justify-between text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 rounded p-1 -m-1"
                    onClick={() => navigateWithBack('/assessments', { priorityFilter: item.label.toLowerCase() })}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: priorityColors[idx] }} />
                      <span className="text-slate-600 dark:text-slate-300">{item.label}</span>
                    </div>
                    <span className="font-semibold text-slate-900 dark:text-white">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Cost by Priority */}
        {(selectedModule === 'all' || selectedModule === 'fca') && (
          <Card className="p-5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <h3 className="font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <DollarSign size={18} className="text-teal-600" />
              Cost by Priority
            </h3>
            <div className="space-y-3">
              <HorizontalBar 
                label="Critical" 
                value={stats?.financials?.cost_by_priority?.critical || 0} 
                maxValue={maxCost} 
                color="bg-red-500"
                onClick={() => navigateWithBack('/assessments', { priorityFilter: 'critical' })}
              />
              <HorizontalBar 
                label="High" 
                value={stats?.financials?.cost_by_priority?.high || 0} 
                maxValue={maxCost} 
                color="bg-orange-500"
                onClick={() => navigateWithBack('/assessments', { priorityFilter: 'high' })}
              />
              <HorizontalBar 
                label="Medium" 
                value={stats?.financials?.cost_by_priority?.medium || 0} 
                maxValue={maxCost} 
                color="bg-yellow-500"
                onClick={() => navigateWithBack('/assessments', { priorityFilter: 'medium' })}
              />
              <HorizontalBar 
                label="Low" 
                value={stats?.financials?.cost_by_priority?.low || 0} 
                maxValue={maxCost} 
                color="bg-green-500"
                onClick={() => navigateWithBack('/assessments', { priorityFilter: 'low' })}
              />
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-700">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Total Backlog</span>
                <span className="font-bold text-slate-900 dark:text-white">
                  {formatCurrency(stats?.financials?.total_deferred_maintenance || 0)}
                </span>
              </div>
            </div>
          </Card>
        )}

        {/* Cost by UNIFORMAT */}
        {(selectedModule === 'all' || selectedModule === 'fca') && (
          <Card className="p-5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <h3 className="font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <BarChart3 size={18} className="text-teal-600" />
              Cost by System (UNIFORMAT)
            </h3>
            <div className="space-y-2">
              {Object.entries(stats?.financials?.cost_by_uniformat || {})
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
                .map(([code, cost], idx) => {
                  const colors = ['bg-teal-500', 'bg-sky-500', 'bg-purple-500', 'bg-indigo-500', 'bg-pink-500'];
                  const maxUniformat = Math.max(...Object.values(stats?.financials?.cost_by_uniformat || {}), 1);
                  return (
                    <HorizontalBar 
                      key={code}
                      label={`${UNIFORMAT_LABELS[code] || code} (${code})`} 
                      value={cost} 
                      maxValue={maxUniformat} 
                      color={colors[idx % colors.length]}
                    />
                  );
                })}
            </div>
          </Card>
        )}

        {/* Work Order Trends */}
        {(selectedModule === 'all' || selectedModule === 'iwms') && (
          <Card className="p-5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <h3 className="font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <TrendingUp size={18} className="text-teal-600" />
              Work Order Trends (6 mo)
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>Created</span>
                  <span className="text-sky-600">
                    {stats?.trends?.work_orders_created?.reduce((sum, d) => sum + d.count, 0) || 0} total
                  </span>
                </div>
                <TrendLine data={stats?.trends?.work_orders_created} color="text-sky-500" />
              </div>
              <div>
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>Completed</span>
                  <span className="text-emerald-600">
                    {stats?.trends?.work_orders_completed?.reduce((sum, d) => sum + d.count, 0) || 0} total
                  </span>
                </div>
                <TrendLine data={stats?.trends?.work_orders_completed} color="text-emerald-500" />
              </div>
            </div>
          </Card>
        )}

        {/* Work Orders by Type */}
        {(selectedModule === 'all' || selectedModule === 'iwms') && (
          <Card className="p-5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <h3 className="font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <Wrench size={18} className="text-teal-600" />
              Work Orders by Type
            </h3>
            <div className="space-y-2">
              {Object.entries(stats?.iwms?.work_orders_by_type || {})
                .filter(([_, count]) => count > 0 || Object.keys(stats?.iwms?.work_orders_by_type || {}).length <= 5)
                .map(([type, count], idx) => {
                const colors = ['bg-purple-500', 'bg-blue-500', 'bg-red-500', 'bg-amber-500', 'bg-slate-500'];
                const maxWO = Math.max(...Object.values(stats?.iwms?.work_orders_by_type || {}), 1);
                const displayLabel = type === 'PM' ? 'Preventive (PM)' : 
                                    type === 'CM' ? 'Corrective (CM)' : type;
                return (
                  <HorizontalBar 
                    key={type}
                    label={displayLabel} 
                    value={count} 
                    maxValue={maxWO} 
                    color={colors[idx % colors.length]}
                    onClick={() => navigateWithBack('/work-orders', { typeFilter: type })}
                  />
                );
              })}
            </div>
          </Card>
        )}

        {/* Work Order Costs */}
        {(selectedModule === 'all' || selectedModule === 'iwms') && (
          <Card className="p-5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <h3 className="font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <DollarSign size={18} className="text-teal-600" />
              Work Order Costs
            </h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <p className="text-xs text-slate-500 dark:text-slate-300 mb-1">Estimated</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white">
                  {formatCurrency(stats?.financials?.work_order_costs?.estimated || 0)}
                </p>
              </div>
              <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <p className="text-xs text-slate-500 dark:text-slate-300 mb-1">Actual</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white">
                  {formatCurrency(stats?.financials?.work_order_costs?.actual || 0)}
                </p>
              </div>
              <div className="p-3 bg-teal-50 dark:bg-teal-900/30 rounded-lg">
                <p className="text-xs text-teal-600 dark:text-teal-400 mb-1">Total</p>
                <p className="text-lg font-bold text-teal-700 dark:text-teal-300">
                  {formatCurrency(stats?.financials?.work_order_costs?.total || 0)}
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Action Items Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Pending Reviews */}
        {(selectedModule === 'all' || selectedModule === 'fca') && (
          <Card className="p-5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <FileCheck size={18} className="text-amber-500" />
                Pending Reviews
              </h3>
              <Badge variant="outline">{stats?.fca?.pending_reviews || 0}</Badge>
            </div>
            {stats?.action_items?.pending_reviews?.length > 0 ? (
              <div className="space-y-2">
                {stats.action_items.pending_reviews.map(review => (
                  <div 
                    key={review.id}
                    onClick={() => navigateWithBack(`/assessments/${review.id}`)}
                    className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                  >
                    <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                      Assessment #{review.id.slice(0, 8)}
                    </span>
                    <ChevronRight size={16} className="text-slate-400" />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">No pending reviews</p>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full mt-3"
              onClick={() => navigateWithBack('/review-queue')}
            >
              View Review Queue
            </Button>
          </Card>
        )}

        {/* High Priority Deficiencies */}
        {(selectedModule === 'all' || selectedModule === 'fca') && (
          <Card className="p-5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <AlertTriangle size={18} className="text-red-500" />
                High Priority Issues
              </h3>
              <Badge variant="destructive">
                {(stats?.fca?.deficiencies_by_priority?.critical || 0) + (stats?.fca?.deficiencies_by_priority?.high || 0)}
              </Badge>
            </div>
            {stats?.action_items?.high_priority_deficiencies?.length > 0 ? (
              <div className="space-y-2">
                {stats.action_items.high_priority_deficiencies.map(def => (
                  <div 
                    key={def.id}
                    className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-lg"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="destructive" className="text-xs">
                        {def.priority}
                      </Badge>
                      <span className="text-xs text-slate-500">
                        {formatCurrency(def.estimated_cost || 0)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 dark:text-slate-300 truncate">
                      {def.description || 'No description'}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">No high priority issues</p>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full mt-3"
              onClick={() => navigateWithBack('/assessments')}
            >
              View All Deficiencies
            </Button>
          </Card>
        )}

        {/* Upcoming PMs */}
        {(selectedModule === 'all' || selectedModule === 'iwms') && (
          <Card className="p-5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <CalendarClock size={18} className="text-purple-500" />
                Upcoming PMs
              </h3>
              <Badge variant="outline">{stats?.iwms?.due_soon_pms || 0} due soon</Badge>
            </div>
            {stats?.action_items?.upcoming_pms?.length > 0 ? (
              <div className="space-y-2">
                {stats.action_items.upcoming_pms.map(pm => (
                  <div 
                    key={pm.id}
                    onClick={() => navigateWithBack(`/pm-schedules`)}
                    className="flex items-center justify-between p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">
                        {pm.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {pm.frequency} • Due {new Date(pm.next_due_date).toLocaleDateString()}
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-slate-400" />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">No upcoming PMs</p>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full mt-3"
              onClick={() => navigateWithBack('/pm-schedules')}
            >
              View PM Schedule
            </Button>
          </Card>
        )}
      </div>

      {/* 20-Year Capital Replacement Forecast */}
      <Card className="mt-6 p-6 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700" data-testid="capital-forecast-card">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-teal-600" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">20-Year Capital Replacement Forecast</h3>
          {(selectedClient || selectedFacility) && (
            <Badge variant="secondary" className="ml-2">Filtered</Badge>
          )}
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-300 mb-4">
          Projected replacement costs based on asset useful life analysis
        </p>
        <ForecastBarChart data={stats?.replacement_forecast || []} height={220} />
        {stats?.replacement_forecast && stats.replacement_forecast.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-teal-500"></div>
                <span className="text-slate-600 dark:text-slate-300">Current Year</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-sky-400"></div>
                <span className="text-slate-600 dark:text-slate-300">Future Years</span>
              </div>
              <div className="ml-auto text-slate-500">
                Total 20-Year Cost: <span className="font-semibold text-slate-900 dark:text-white">
                  {formatCurrency(stats.replacement_forecast.reduce((sum, item) => sum + (item.cost || 0), 0))}
                </span>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Quick Stats Footer */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <Link to="/clients">
          <Card className="p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-teal-300 transition-all text-center">
            <Briefcase size={18} className="mx-auto text-teal-600 mb-1" />
            <p className="text-xl font-bold text-slate-900 dark:text-white">{stats?.clients || 0}</p>
            <p className="text-xs text-slate-500">Clients</p>
          </Card>
        </Link>
        <Link to="/facilities">
          <Card className="p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-teal-300 transition-all text-center">
            <Building2 size={18} className="mx-auto text-sky-600 mb-1" />
            <p className="text-xl font-bold text-slate-900 dark:text-white">{stats?.facilities || 0}</p>
            <p className="text-xs text-slate-500">Facilities</p>
          </Card>
        </Link>
        <Link to="/assessments">
          <Card className="p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-teal-300 transition-all text-center">
            <ClipboardList size={18} className="mx-auto text-purple-600 mb-1" />
            <p className="text-xl font-bold text-slate-900 dark:text-white">{stats?.fca?.total_assessments || 0}</p>
            <p className="text-xs text-slate-500">Assessments</p>
          </Card>
        </Link>
        <Link to="/assessments">
          <Card className="p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-teal-300 transition-all text-center">
            <AlertCircle size={18} className="mx-auto text-red-500 mb-1" />
            <p className="text-xl font-bold text-slate-900 dark:text-white">{stats?.fca?.total_deficiencies || 0}</p>
            <p className="text-xs text-slate-500">Deficiencies</p>
          </Card>
        </Link>
        <Link to="/work-orders">
          <Card className="p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-teal-300 transition-all text-center">
            <Wrench size={18} className="mx-auto text-indigo-600 mb-1" />
            <p className="text-xl font-bold text-slate-900 dark:text-white">{stats?.iwms?.total_work_orders || 0}</p>
            <p className="text-xs text-slate-500">Work Orders</p>
          </Card>
        </Link>
        <Link to="/pm-schedules">
          <Card className="p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-teal-300 transition-all text-center">
            <CalendarClock size={18} className="mx-auto text-amber-600 mb-1" />
            <p className="text-xl font-bold text-slate-900 dark:text-white">{stats?.iwms?.total_pm_schedules || 0}</p>
            <p className="text-xs text-slate-500">PM Schedules</p>
          </Card>
        </Link>
        <Link to="/warranties">
          <Card className="p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-teal-300 transition-all text-center">
            <Shield size={18} className="mx-auto text-emerald-600 mb-1" />
            <p className="text-xl font-bold text-slate-900 dark:text-white">{stats?.iwms?.total_warranties || 0}</p>
            <p className="text-xs text-slate-500">Warranties</p>
          </Card>
        </Link>
        <Link to="/vendors">
          <Card className="p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-teal-300 transition-all text-center">
            <Users size={18} className="mx-auto text-pink-600 mb-1" />
            <p className="text-xl font-bold text-slate-900 dark:text-white">{stats?.iwms?.vendor_count || 0}</p>
            <p className="text-xs text-slate-500">Vendors</p>
          </Card>
        </Link>
      </div>

      {/* Last Updated */}
      <div className="mt-4 text-center text-xs text-slate-400">
        Last updated: {stats?.generated_at ? new Date(stats.generated_at).toLocaleString() : 'N/A'}
      </div>
    </div>
  );
};

export default DashboardPage;
